import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { SyncButton } from '../../components/SyncButton';
import {
  getIntegrationsQuickbooks,
  postIntegrationsQuickbooks,
  deleteIntegrationsQuickbooksIntegrationId,
  postIntegrationsQuickbooksIntegrationIdSync,
  postIntegrationsQuickbooksIntegrationIdRefreshInfo,
  postIntegrationsQuickbooksIntegrationIdTestConnection,
  postIntegrationsQuickbooksIntegrationIdReauthorize,
  putIntegrationsQuickbooksIntegrationId,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { useNotifications } from '../../contexts/NotificationContext';
import type { QuickBooksIntegration } from '../../api/generated/model';

function computeNextSyncTime(lastSyncAt: string | null | undefined, frequency: string, timeOfDay: string): Date | null {
  const now = new Date();
  const [hour, minute] = (timeOfDay || '02:00').split(':').map(Number);

  if (!lastSyncAt) return now; // never synced, due immediately

  const last = new Date(lastSyncAt);

  let next: Date;
  switch (frequency) {
    case 'hourly': {
      next = new Date(last);
      next.setMinutes(0, 0, 0);
      next.setHours(next.getHours() + 1);
      while (next <= last || next < now) {
        next.setHours(next.getHours() + 1);
      }
      break;
    }
    case 'daily': {
      next = new Date(last);
      next.setHours(hour, minute, 0, 0);
      if (next <= last) next.setDate(next.getDate() + 1);
      while (next < now) next.setDate(next.getDate() + 1);
      break;
    }
    case 'weekly': {
      next = new Date(last);
      next.setHours(hour, minute, 0, 0);
      const daysUntilMonday = (1 - next.getDay() + 7) % 7 || (next <= last ? 7 : 0);
      next.setDate(next.getDate() + daysUntilMonday);
      while (next < now) next.setDate(next.getDate() + 7);
      break;
    }
    case 'monthly': {
      next = new Date(last.getFullYear(), last.getMonth() + 1, 1, hour, minute, 0, 0);
      while (next < now) next.setMonth(next.getMonth() + 1);
      break;
    }
    default:
      return null;
  }
  return next;
}

function formatNextSync(date: Date): string {
  return date.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

interface PeriodicSyncSettingsProps {
  integration: QuickBooksIntegration;
  onSave: (update: Record<string, any>) => Promise<void>;
}

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const PeriodicSyncSettings: React.FC<PeriodicSyncSettingsProps> = ({ integration, onSave }) => {
  const [enabled, setEnabled] = useState(integration.periodic_sync_enabled || false);
  const [frequency, setFrequency] = useState<string>(integration.periodic_sync_frequency || 'daily');
  const [timeOfDay, setTimeOfDay] = useState(integration.periodic_sync_time_of_day || '02:00');
  const [dayOfWeek, setDayOfWeek] = useState(integration.periodic_sync_day_of_week ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(integration.periodic_sync_day_of_month ?? 1);
  const [timezone, setTimezone] = useState(integration.periodic_sync_timezone || browserTimezone);
  const [saving, setSaving] = useState(false);

  const isActive = integration.status === 'active';
  const isDirty = enabled !== (integration.periodic_sync_enabled || false)
    || frequency !== (integration.periodic_sync_frequency || 'daily')
    || timeOfDay !== (integration.periodic_sync_time_of_day || '02:00')
    || dayOfWeek !== (integration.periodic_sync_day_of_week ?? 1)
    || dayOfMonth !== (integration.periodic_sync_day_of_month ?? 1)
    || timezone !== (integration.periodic_sync_timezone || browserTimezone);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        periodic_sync_enabled: enabled,
        periodic_sync_frequency: frequency,
        periodic_sync_time_of_day: timeOfDay,
        periodic_sync_day_of_week: dayOfWeek,
        periodic_sync_day_of_month: dayOfMonth,
        periodic_sync_timezone: timezone,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEnabled(integration.periodic_sync_enabled || false);
    setFrequency(integration.periodic_sync_frequency || 'daily');
    setTimeOfDay(integration.periodic_sync_time_of_day || '02:00');
    setDayOfWeek(integration.periodic_sync_day_of_week ?? 1);
    setDayOfMonth(integration.periodic_sync_day_of_month ?? 1);
    setTimezone(integration.periodic_sync_timezone || browserTimezone);
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <h6 className="mb-2">Periodic Sync Schedule</h6>
      <div className="row g-3 align-items-center">
        <div className="col-auto">
          <Form.Check
            id={`periodic-sync-${integration.id}`}
            type="checkbox"
            label="Enable Periodic Sync"
            checked={enabled}
            onChange={() => setEnabled(!enabled)}
            disabled={!isActive}
          />
        </div>
        <div className="col-auto">
          <Form.Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            disabled={!enabled || !isActive}
            size="sm"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </Form.Select>
        </div>
        {frequency === 'weekly' && (
          <div className="col-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">on</span>
              <Form.Select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                disabled={!enabled || !isActive}
                size="sm"
                style={{ width: '130px' }}
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </Form.Select>
            </div>
          </div>
        )}
        {frequency === 'monthly' && (
          <div className="col-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">on day</span>
              <Form.Select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                disabled={!enabled || !isActive}
                size="sm"
                style={{ width: '70px' }}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Form.Select>
            </div>
          </div>
        )}
        {frequency !== 'hourly' && (
          <div className="col-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">at</span>
              <Form.Control
                type="time"
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
                disabled={!enabled || !isActive}
                size="sm"
                style={{ width: '120px' }}
              />
            </div>
          </div>
        )}
        {frequency !== 'hourly' && (
          <div className="col-auto">
            <Form.Select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!enabled || !isActive}
              size="sm"
              style={{ width: '220px' }}
            >
              {((Intl as any).supportedValuesOf('timeZone') as string[]).map(tz => (
                <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
              ))}
            </Form.Select>
          </div>
        )}
        {isDirty && (
          <div className="col-auto">
            <div className="d-flex gap-2">
              <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        {!isDirty && integration.periodic_sync_enabled && (() => {
          const next = computeNextSyncTime(
            integration.last_sync_at,
            integration.periodic_sync_frequency || 'daily',
            integration.periodic_sync_time_of_day || '02:00',
          );
          return next ? (
            <div className="col-auto">
              <span className="text-muted small">(Next sync at {formatNextSync(next)})</span>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

const QuickBooksTab: React.FC = () => {
  const [quickbooksIntegrations, setQuickbooksIntegrations] = useState<any[]>([]);
  const [isLoadingQBIntegrations, setIsLoadingQBIntegrations] = useState(false);
  const [qbError, setQBError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showAddIntegration, setShowAddIntegration] = useState(false);
  const [newInvoiceSuffix, setNewInvoiceSuffix] = useState('');

  // Get active syncs to monitor completion
  const { activeSyncs } = useNotifications();

  // Ref to track previous sync states
  const previousSyncStatesRef = useRef<Map<string, string>>(new Map());

  // Fetch integrations on mount
  useEffect(() => {
    fetchQuickBooksIntegrations();
  }, []);

  // Monitor sync completion and refetch integration data
  useEffect(() => {
    activeSyncs.forEach((syncEvent, syncId) => {
      const prevStatus = previousSyncStatesRef.current.get(syncId);
      const currentStatus = syncEvent.status;

      // Detect completion, failure, or error (transition from started/progress to terminal state)
      if ((prevStatus === 'started' || prevStatus === 'progress') &&
          (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'error')) {

        const integrationId = syncEvent.integration_id;

        // Refetch QuickBooks integrations if this was a QB sync
        if (quickbooksIntegrations.some(int => int.id === integrationId)) {
          console.log(`[QuickBooksTab] QB sync ${currentStatus} for ${integrationId}, refetching integrations`);
          fetchQuickBooksIntegrations();

          if (currentStatus === 'error' && syncEvent.error) {
            setQBError(`Sync failed: ${syncEvent.error}`);
          }
        }
      }

      // Update the tracked state
      previousSyncStatesRef.current.set(syncId, currentStatus);
    });
  }, [activeSyncs, quickbooksIntegrations]);

  const fetchQuickBooksIntegrations = async () => {
    setIsLoadingQBIntegrations(true);
    setQBError(null);
    try {
      const response = await getIntegrationsQuickbooks();
      setQuickbooksIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching QuickBooks integrations:', error);
      setQBError('Failed to fetch QuickBooks integrations');
    } finally {
      setIsLoadingQBIntegrations(false);
    }
  };

  const handleQuickBooksConnect = async () => {
    setIsConnecting(true);
    setQBError(null);
    try {
      if (!newInvoiceSuffix.trim()) {
        setQBError('Please enter an invoice suffix');
        setIsConnecting(false);
        return;
      }

      const response = await postIntegrationsQuickbooks({
        invoice_suffix: newInvoiceSuffix.trim()
      });
      // Open QuickBooks OAuth URL in a new window
      if (response.auth_url) {
        const authWindow = window.open(response.auth_url, 'QuickBooks Auth', 'width=800,height=600');

        // Listen for success message from popup
        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'QUICKBOOKS_AUTH_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            setShowAddIntegration(false);
            setNewInvoiceSuffix('');
            fetchQuickBooksIntegrations();
          }
        };

        window.addEventListener('message', handleMessage);

        // Fallback: Poll for window closure (in case postMessage doesn't work)
        const pollInterval = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(pollInterval);
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            // Refresh status after window closes
            fetchQuickBooksIntegrations();
          }
        }, 1000);

        // Cleanup after 5 minutes (in case window doesn't close)
        setTimeout(() => {
          clearInterval(pollInterval);
          window.removeEventListener('message', handleMessage);
          if (!authWindow?.closed) {
            authWindow?.close();
          }
          setIsConnecting(false);
        }, 300000);
      }
    } catch (error: any) {
      console.error('Error initiating QuickBooks connection:', error);
      if (error.response?.status === 400) {
        setQBError('Invoice suffix already exists or is invalid');
      } else {
        setQBError('Failed to initiate QuickBooks connection');
      }
      setIsConnecting(false);
    }
  };

  const handleDeleteIntegration = async (integrationId: string, invoiceSuffix: string) => {
    if (!window.confirm(`Are you sure you want to delete the QuickBooks integration "${invoiceSuffix}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsQuickbooksIntegrationId(integrationId);
      await fetchQuickBooksIntegrations();
    } catch (error) {
      console.error('Error deleting QuickBooks integration:', error);
      setQBError('Failed to delete QuickBooks integration');
    }
  };

  const handleSyncIntegration = async (integrationId: string, invoiceSuffix: string, force?: boolean): Promise<string | undefined> => {
    try {
      const resp = await postIntegrationsQuickbooksIntegrationIdSync(integrationId, force ? { force: true } : undefined);
      // Background the integrations refresh so the SyncButton can subscribe to
      // the new sync_id immediately and start showing progress.
      fetchQuickBooksIntegrations();
      return (resp as any)?.sync_id;
    } catch (error: any) {
      console.error('Error triggering QuickBooks sync:', error);
      fetchQuickBooksIntegrations();
      return undefined;
    }
  };

  const handleRefreshCompanyInfo = async (integrationId: string, invoiceSuffix: string) => {
    try {
      await postIntegrationsQuickbooksIntegrationIdRefreshInfo(integrationId);
      await fetchQuickBooksIntegrations();
    } catch (error: any) {
      console.error('Error refreshing company info:', error);
      await fetchQuickBooksIntegrations();
    }
  };

  const handleTestQuickBooksConnection = async (integrationId: string, invoiceSuffix: string) => {
    try {
      await postIntegrationsQuickbooksIntegrationIdTestConnection(integrationId);
      await fetchQuickBooksIntegrations();
    } catch (error: any) {
      console.error('Error testing QuickBooks connection:', error);
      await fetchQuickBooksIntegrations();
    }
  };

  const handleUpdateIntegration = async (integrationId: string, update: Record<string, any>) => {
    try {
      await putIntegrationsQuickbooksIntegrationId(integrationId, update);
      await fetchQuickBooksIntegrations();
    } catch (error) {
      console.error('Error updating integration settings:', error);
      setQBError('Failed to update integration settings');
    }
  };

  const handleReauthorize = async (integrationId: string) => {
    setIsConnecting(true);
    setQBError(null);
    try {
      const response = await postIntegrationsQuickbooksIntegrationIdReauthorize(integrationId);
      if (response.auth_url) {
        const authWindow = window.open(response.auth_url, 'QuickBooks Auth', 'width=800,height=600');

        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'QUICKBOOKS_AUTH_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            fetchQuickBooksIntegrations();
          }
        };

        window.addEventListener('message', handleMessage);

        const pollInterval = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(pollInterval);
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            fetchQuickBooksIntegrations();
          }
        }, 1000);

        setTimeout(() => {
          clearInterval(pollInterval);
          window.removeEventListener('message', handleMessage);
          if (!authWindow?.closed) {
            authWindow?.close();
          }
          setIsConnecting(false);
        }, 300000);
      }
    } catch (error: any) {
      console.error('Error initiating QuickBooks reauthorization:', error);
      setQBError('Failed to initiate QuickBooks reauthorization');
      setIsConnecting(false);
    }
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="me-5 bg-header-control rounded p-2">
                <img src="/quickbooks-brand-preferred-logo-50-50-black-external.png" alt="QuickBooks" style={{ height: '3rem' }} />
              </div>
              <div>
                <h5 className="mb-1">QuickBooks Integrations</h5>
                <p className="text-muted mb-0">Connect One or more QuickBooks accounts</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => setShowAddIntegration(true)}
              disabled={isConnecting}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Integration
            </Button>
          </div>

          {qbError && (
            <Alert variant="danger" dismissible onClose={() => setQBError(null)}>
              {qbError}
            </Alert>
          )}


          {isLoadingQBIntegrations && quickbooksIntegrations.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integrations...</p>
            </div>
          ) : quickbooksIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <div className="d-inline-block bg-header-control rounded p-2">
                <img src="/quickbooks-brand-preferred-logo-50-50-black-external.png" alt="QuickBooks" style={{ height: '4rem' }} />
              </div>
              <h6 className="mt-3 text-muted">No QuickBooks Integrations</h6>
              <p className="text-muted">Add your first QuickBooks integration to get started</p>
            </div>
          ) : (
            <div className="row">
              {quickbooksIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-6 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.company_name || 'QuickBooks Company'}
                          </h6>
                          {integration.legal_name && integration.legal_name !== integration.company_name && (
                            <div className="text-muted small mb-1">
                              <i className="bi bi-building me-1"></i>
                              Legal Name: {integration.legal_name}
                            </div>
                          )}
                          <div className="text-muted small">
                            <i className="bi bi-hash me-1"></i>
                            Company ID: {integration.realm_id}
                          </div>
                          <div className="text-muted small">
                            <i className="bi bi-tag me-1"></i>
                            Invoice Suffix: {integration.invoice_suffix}
                          </div>
                        </div>
                        <span className={`badge ${
                          integration.status === 'active' ? 'bg-success' :
                          integration.status === 'pending' ? 'bg-warning' : 'bg-secondary'
                        }`}>
                          {integration.status === 'active' ? 'Connected' :
                           integration.status === 'pending' ? 'Pending' : 'Inactive'}
                        </span>
                      </div>

                      {/* Company Contact Information */}
                      <div className="mb-3">
                        <div className="row">
                          <div className="col">
                            {integration.contact_email && (
                              <div className="text-muted small mb-1">
                                <i className="bi bi-envelope me-1"></i>
                                {integration.contact_email}
                              </div>
                            )}
                            {integration.phone && (
                              <div className="text-muted small mb-1">
                                <i className="bi bi-telephone me-1"></i>
                                {integration.phone}
                              </div>
                            )}
                            {integration.website && (
                              <div className="text-muted small mb-1">
                                <i className="bi bi-globe me-1"></i>
                                <a href={integration.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                  {integration.website}
                                </a>
                              </div>
                            )}
                            {integration.address && (
                              <div className="text-muted small">
                                <i className="bi bi-geo-alt me-1"></i>
                                {integration.address}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3 pt-2 border-top">
                        <small className="text-muted d-block">Last Sync:</small>
                        <small>
                          {integration.last_sync_at
                            ? new Date(integration.last_sync_at).toLocaleString()
                            : 'Never'
                          }
                        </small>
                      </div>

                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestQuickBooksConnection(integration.id, integration.invoice_suffix)}
                          disabled={integration.status !== 'active'}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Test Connection
                        </Button>
                        <SyncButton
                          claimId={`qb-sync-${integration.id}`}
                          onClick={() => handleSyncIntegration(integration.id, integration.invoice_suffix)}
                          disabled={integration.status !== 'active'}
                        />
                        <SyncButton
                          claimId={`qb-force-sync-${integration.id}`}
                          variant="outline-warning"
                          icon="bi-arrow-repeat"
                          buttonText="Force Sync"
                          onClick={() => handleSyncIntegration(integration.id, integration.invoice_suffix, true)}
                          disabled={integration.status !== 'active'}
                        />
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleRefreshCompanyInfo(integration.id, integration.invoice_suffix)}
                          disabled={integration.status !== 'active'}
                        >
                          <i className="bi bi-building me-1"></i>
                          Refresh Info
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleReauthorize(integration.id)}
                          disabled={integration.status !== 'active' || isConnecting}
                        >
                          <i className="bi bi-key me-1"></i>
                          Reauthorize
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteIntegration(integration.id, integration.invoice_suffix)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-top">
                        <div className="row">
                          <div className="col">
                            <Form.Check
                              id={`sync-invoices-${integration.id}`}
                              type="checkbox"
                              label="Sync Invoices"
                              checked={integration.sync_invoices}
                              onChange={() => handleUpdateIntegration(integration.id, { sync_invoices: !integration.sync_invoices })}
                              disabled={integration.status !== 'active'}
                            />
                          </div>
                          <div className="col">
                            <Form.Check
                              id={`sync-payments-${integration.id}`}
                              type="checkbox"
                              label="Sync Payments"
                              checked={integration.sync_payments}
                              onChange={() => handleUpdateIntegration(integration.id, { sync_payments: !integration.sync_payments })}
                              disabled={integration.status !== 'active'}
                            />
                          </div>
                          <div className="col">
                            <Form.Check
                              id={`sync-customers-${integration.id}`}
                              type="checkbox"
                              label="Sync Customers"
                              checked={integration.sync_customers}
                              onChange={() => handleUpdateIntegration(integration.id, { sync_customers: !integration.sync_customers })}
                              disabled={integration.status !== 'active'}
                            />
                          </div>
                          <div className="col">
                            <Form.Check
                              id={`sync-employees-${integration.id}`}
                              type="checkbox"
                              label="Sync Employees"
                              checked={integration.sync_employees}
                              onChange={() => handleUpdateIntegration(integration.id, { sync_employees: !integration.sync_employees })}
                              disabled={integration.status !== 'active'}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Periodic Sync Schedule */}
                      <PeriodicSyncSettings
                        integration={integration}
                        onSave={(update) => handleUpdateIntegration(integration.id, update)}
                      />
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add QuickBooks Integration Modal */}
      <Modal show={showAddIntegration} onHide={() => {
        setShowAddIntegration(false);
        setNewInvoiceSuffix('');
        setQBError(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New QuickBooks Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {qbError && (
            <Alert variant="danger" dismissible onClose={() => setQBError(null)}>
              {qbError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="me-2">Invoice Suffix <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., MAIN"
              value={newInvoiceSuffix}
              onChange={(e) => setNewInvoiceSuffix(e.target.value.toUpperCase())}
              maxLength={10}
              style={{ width: '150px' }}
              className="d-inline-block ms-2"
            />
            <Form.Text className="text-muted d-block">
              Unique identifier to differentiate invoices (1-10 characters)
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAddIntegration(false);
              setNewInvoiceSuffix('');
              setQBError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleQuickBooksConnect}
            disabled={isConnecting || !newInvoiceSuffix.trim()}
          >
            {isConnecting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Connecting...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Connect to QuickBooks
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuickBooksTab;
